import useEditRole from 'api/accessControl/useEditRole/useEditRole';
import { useGetBaseRoleList } from 'api/accessControl/useGetBaseRoleList/useGetBaseRoleList';
import useInsertRole from 'api/accessControl/useInsertRole/useInsertRole';
import { EditRoleModal } from 'components/private/Modals/accessControl/EditRoleModal/EditRoleModal';
import RoleInsertModal from 'components/private/Modals/accessControl/RoleInsertModal/RoleInsertModal';
import ApiPaginateTable from 'components/shared/Table/ApiPaginateTable';
import useBaseRoleTableColumns from 'hooks/accessControl/useBaseRoleTableColumns';
import { permissions } from 'hooks/permissions/permissions';
import { usePermissions } from 'hooks/permissions/usePermissions';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import PageSpinner from '../../components/shared/pageSpinner/PageSpinner';

const BASE_ROLE_ENUM = 0; // 0=base role, 1=groupPermission

const BaseRolesPage = () => {
  const { can } = usePermissions();

  const baseRolesPermissions =
    permissions.general.categories.keySettings.accessLevel.baseRoles;

  const canCreate = can(baseRolesPermissions.create);
  const canEdit = can(baseRolesPermissions.edit);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState('100');
  const [selectedRows, setSelectedRows] = useState([]);
  const [getBaseRoleList, loading, roleList, pageRef] = useGetBaseRoleList();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isInsertModalOpen, setIsInsertModalOpen] = useState(false);
  const [roleEditValues, setRoleEditValues] = useState({});
  const [insertInputValue, setInsertInputValue] = useState('');

  const [editRequest, editLoading] = useEditRole();
  const [insertRequest, insertLoading] = useInsertRole();

  const tableRef = useRef(null);

  useEffect(() => {
    getBaseRoleList({ page: 1, count: pageSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getBaseRoleList({ page, count: pageSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  const editItem = useCallback(
    row => () => {
      setRoleEditValues(row);
      setIsEditModalOpen(true);
    },
    [],
  );

  const [columnsData] = useBaseRoleTableColumns(editItem, tableRef, {
    canEdit,
  });

  const editHandle = data => {
    if (data) {
      editRequest(
        {
          guid: roleEditValues.id,
          name: data.title,
          display: data.display,
          type: BASE_ROLE_ENUM,
        },
        getBaseRoleList,
        setIsEditModalOpen,
      );
    }
  };

  const insertHandle = payload => {
    insertRequest(
      () => getBaseRoleList({ page, count: pageSize }),
      setIsInsertModalOpen,
      {
        name: payload.name,
        display: payload.display,
        type: BASE_ROLE_ENUM,
      },
    );
  };

  const closeEditModal = () => setIsEditModalOpen(false);

  function getPageData(pageNumber) {
    setPage(pageNumber);
    getBaseRoleList({ page: pageNumber, count: pageSize });
  }

  return (
    <>
      <div>{pageRef.refTitle}</div>
      {Object.keys(roleList).length !== 0 ? (
        <ApiPaginateTable
          columns={columnsData}
          rowsData={roleList[page]}
          rows={roleList[page]}
          pageSize={pageSize}
          isDeletable={false}
          isLoading={loading}
          addLable={canCreate ? 'ایجاد نقش پایه‌ جدید' : undefined}
          onAddClick={canCreate ? () => setIsInsertModalOpen(true) : undefined}
          setPageSize={val => setPageSize(val)}
          apiRef={tableRef}
          onPageChange={p => getPageData(p)}
          page={page}
          countPages={
            pageRef?.totalCount ? Math.ceil(pageRef.totalCount / pageSize) : 1
          }
          setSelectedRows={rows => setSelectedRows(rows)}
          selectedRows={selectedRows}
        />
      ) : (
        <PageSpinner loading />
      )}
      {canEdit ? (
        <EditRoleModal
          open={isEditModalOpen}
          handleClose={closeEditModal}
          editLoading={editLoading}
          onEdit={editHandle}
          initialValues={roleEditValues}
        />
      ) : null}
      {canCreate ? (
        <RoleInsertModal
          open={isInsertModalOpen}
          handleClose={() => setIsInsertModalOpen(false)}
          insertLoading={insertLoading}
          onInsert={insertHandle}
          insertInputValue={insertInputValue}
          insertInputOnchange={e => setInsertInputValue(e.target.value)}
        />
      ) : null}
    </>
  );
};

export default BaseRolesPage;
